/**
 * useStandardActions — Apply the Standard draft to its tracker (Voluum, or
 * Tres Commas through /tres-commas/flows/:id/raw).
 * For Media Devoted | Created By Ciro Autiero
 *
 * Manual snapshots and restore live in the Snapshots panel (drawer footer).
 */

import { useCallback } from 'react';

import { trackerLabel, type TrackerId, type VoluumFlowUpdate } from '@trafficpulse/shared';

import {
  formatFlowApplyFailure,
  type FlowApplyFailureNotice,
} from '../flow-apply';

import { useToast } from '@/providers/ToastProvider';
import { useUpdateRawFlow } from '../../../../hooks';

export interface UseStandardActionsParams {
  flowId: string;
  provider: TrackerId;
}

export type StandardApplyResult =
  | { ok: true }
  | { ok: false; notice: FlowApplyFailureNotice };

export interface UseStandardActionsResult {
  applying: boolean;
  apply: (draft: VoluumFlowUpdate) => Promise<StandardApplyResult>;
}

export function useStandardActions(
  params: UseStandardActionsParams,
): UseStandardActionsResult {
  const toast = useToast();
  const updateMutation = useUpdateRawFlow(params.flowId, params.provider);
  const trackerName = trackerLabel(params.provider);

  const apply = useCallback(
    async (draft: VoluumFlowUpdate): Promise<StandardApplyResult> => {
      try {
        await updateMutation.mutateAsync(draft);
        toast.success(
          'Flow updated',
          'Snapshot taken automatically before push.',
        );
        return { ok: true };
      } catch (error) {
        const notice = formatFlowApplyFailure(error, trackerName);
        toast.error(notice.title, notice.lines.join('\n'));
        return { ok: false, notice };
      }
    },
    [toast, updateMutation, trackerName],
  );

  return {
    applying: updateMutation.isPending,
    apply,
  };
}
